import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { AdminApiError, adminErrorMessage } from './api';
import { ComponentRegistry } from './ComponentRegistry';
import { fetchAdminUserDetails } from './historyApi';
import { HISTORY_LABELS, roleLabels, userStatusLabels, valueLabel } from './labels';
import type { AdminUserDetails, UserHistoryTab } from './types';
import { formatUnits } from '../../core/money';
import styles from './admin.module.css';

function date(value: string | null): string {
  if (value === null) return '未验证';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString();
}

export function UserDetails({ userId, onClose, onHistory, onAccessFailure }: {
  userId: string; onClose: () => void; onHistory: (tab: UserHistoryTab, userId: string) => void; onAccessFailure: (failure: AdminApiError) => void;
}): ReactElement {
  const [details, setDetails] = useState<AdminUserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const abort = new AbortController();
    setLoading(true); setDetails(null); setError('');
    void fetchAdminUserDetails(userId, abort.signal).then((next) => {
      if (!abort.signal.aborted) setDetails(next);
    }).catch((failure: unknown) => {
      if (abort.signal.aborted) return;
      if (failure instanceof AdminApiError && (failure.status === 401 || failure.status === 403)) onAccessFailure(failure);
      else setError(failure instanceof AdminApiError && failure.status === 404 ? '用户已不存在。' : adminErrorMessage(failure));
    }).finally(() => { if (!abort.signal.aborted) setLoading(false); });
    return () => abort.abort();
  }, [userId, revision, onAccessFailure]);

  return <ComponentRegistry id="admin.users.details" className={styles.details}>
    <section aria-label="用户详情" aria-busy={loading}>
      <div className={styles.toolbar}><h3>用户详情</h3><button type="button" onClick={onClose}>关闭详情</button></div>
      <ComponentRegistry id="admin.users.details.status">
        {loading && <p role="status">正在加载用户详情…</p>}
        {error && <><p role="alert" className={styles.error}>{error}</p><button type="button" onClick={() => setRevision((value) => value + 1)}>重试加载详情</button></>}
      </ComponentRegistry>
      {details && <>
        <ComponentRegistry id="admin.users.details.fields">
          <dl className={styles.detailFields}>
            <div><dt>邮箱</dt><dd>{details.user.email}</dd></div><div><dt>用户 ID</dt><dd>{details.user.id}</dd></div>
            <div><dt>角色</dt><dd>{valueLabel(roleLabels, details.user.role)}</dd></div><div><dt>状态</dt><dd>{valueLabel(userStatusLabels, details.user.status)}</dd></div>
            <div><dt>创建时间</dt><dd>{date(details.user.createdAt)}</dd></div><div><dt>邮箱验证时间</dt><dd>{date(details.user.emailVerifiedAt)}</dd></div>
            <div><dt>更新时间</dt><dd>{date(details.user.updatedAt)}</dd></div>
            <div><dt>钱包余额</dt><dd>{details.wallet === null ? '无钱包记录，余额不可用。' : `${formatUnits(details.wallet.balanceUnits)} 元 · ${details.wallet.unit}`}</dd></div>
            {details.wallet && <div><dt>余额更新时间</dt><dd>{date(details.wallet.updatedAt)}</dd></div>}
          </dl>
        </ComponentRegistry>
        <ComponentRegistry id="admin.users.details.history" className={styles.actions}>
          {(['Ledger', 'Usage', 'Orders'] as const).map((tab) => <button key={tab} type="button" onClick={() => onHistory(tab, details.user.id)}>查看用户{HISTORY_LABELS[tab]}</button>)}
        </ComponentRegistry>
      </>}
    </section>
  </ComponentRegistry>;
}
